// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import gsap from 'gsap'; // Import gsap
import { Flip } from 'gsap/Flip'; // Import Flip
import { ScrollTrigger } from 'gsap/ScrollTrigger'; //Import ScrollTrigger
import './App.css';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header';
import Hero from './components/Hero';
import PortfolioSection from './components/PortfolioSection';
import About from './components/About';
import Services from './components/Services';
import WhyChooseUs from './components/WhyChooseUs';

import Contact from './components/Contact';
import Footer from './components/Footer';
import ReviewsModal from './components/ReviewsModal';
import PortfolioModal from './components/PortfolioModal';
import CustomerGallery from './pages/CustomerGallery';

// --- REGISTER GSAP PLUGINS GLOBALLY ---
gsap.registerPlugin(Flip, ScrollTrigger);
// ------------------------------------

function MainPage({
  activeFloatingButtonId,
  setActiveFloatingButtonId,
  isHeroEligibleToFloat,
  setIsHeroEligibleToFloat,
  isPortfolioEligibleToFloat,
  setIsPortfolioEligibleToFloat,
  isReviewsModalOpen,
  setIsReviewsModalOpen,
  isPortfolioModalOpen,
  setIsPortfolioModalOpen,
  openReviewsModal,
  closeReviewsModal,
  openPortfolioModal,
  closePortfolioModal,
  showInstallButton,
  handleInstallClick
}) {
  return (
    <>
      <Header
        openReviewsModal={openReviewsModal}
        openPortfolioModal={openPortfolioModal}
      />
      <main>
        <Hero
          setIsEligible={setIsHeroEligibleToFloat}
          isActuallyFloating={activeFloatingButtonId === 'hero'}
        />
        <PortfolioSection
          openPortfolioModal={openPortfolioModal}
          setIsEligible={setIsPortfolioEligibleToFloat}
          isActuallyFloating={activeFloatingButtonId === 'portfolio'}
        />
        <Services />
        <About />
        <WhyChooseUs />
        <Contact />
      </main>
      <Footer />

      {/* PWA 설치 버튼 (optional) */}
      {showInstallButton && (
        <button
          className={`pwa-install-button ${isPortfolioModalOpen || isReviewsModalOpen ? 'hidden' : ''}`}
          onClick={handleInstallClick}
          disabled={isPortfolioModalOpen || isReviewsModalOpen}
        >
          앱 설치하기
        </button>
      )}

      {/* Modals */}
      {isReviewsModalOpen && <ReviewsModal onClose={closeReviewsModal} />}
      {isPortfolioModalOpen && <PortfolioModal onClose={closePortfolioModal} />}
    </>
  );
}

function App() {
  // --- State for Floating Button Control ---
  const [activeFloatingButtonId, setActiveFloatingButtonId] = useState(null);
  const [isHeroEligibleToFloat, setIsHeroEligibleToFloat] = useState(false);
  const [isPortfolioEligibleToFloat, setIsPortfolioEligibleToFloat] = useState(false);

  // Modal State
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);

  // PWA Install State
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // PWA Install Logic
  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setShowInstallButton(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    await installPromptEvent.userChoice;
    setInstallPromptEvent(null);
    setShowInstallButton(false);
  };

  // Modal Control Functions
  const openReviewsModal = () => setIsReviewsModalOpen(true);
  const closeReviewsModal = () => setIsReviewsModalOpen(false);
  const openPortfolioModal = useCallback(() => setIsPortfolioModalOpen(true), []);
  const closePortfolioModal = () => setIsPortfolioModalOpen(false);

  // Logic to determine which button should float
  useEffect(() => {
    if (isPortfolioEligibleToFloat) {
      setActiveFloatingButtonId('portfolio');
    } else if (isHeroEligibleToFloat) {
      setActiveFloatingButtonId('hero');
    } else {
      setActiveFloatingButtonId(null);
    }
  }, [isHeroEligibleToFloat, isPortfolioEligibleToFloat]);

  return (
    <AuthProvider>
      <HashRouter>
        <div className="App">
          <Routes>
            <Route
              path="/"
              element={
                <MainPage
                  activeFloatingButtonId={activeFloatingButtonId}
                  setActiveFloatingButtonId={setActiveFloatingButtonId}
                  isHeroEligibleToFloat={isHeroEligibleToFloat}
                  setIsHeroEligibleToFloat={setIsHeroEligibleToFloat}
                  isPortfolioEligibleToFloat={isPortfolioEligibleToFloat}
                  setIsPortfolioEligibleToFloat={setIsPortfolioEligibleToFloat}
                  isReviewsModalOpen={isReviewsModalOpen}
                  setIsReviewsModalOpen={setIsReviewsModalOpen}
                  isPortfolioModalOpen={isPortfolioModalOpen}
                  setIsPortfolioModalOpen={setIsPortfolioModalOpen}
                  openReviewsModal={openReviewsModal}
                  closeReviewsModal={closeReviewsModal}
                  openPortfolioModal={openPortfolioModal}
                  closePortfolioModal={closePortfolioModal}
                  showInstallButton={showInstallButton}
                  handleInstallClick={handleInstallClick}
                />
              }
            />
            <Route path="/gallery/:customerId" element={<CustomerGallery />} />
          </Routes>
        </div>
      </HashRouter>
    </AuthProvider>
  );
}

export default App;